import { useMemo, useRef } from 'react';

import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import type { RovoSkillsInsertMenuConfig } from '@atlassian/rovo-skills/insert-menu-provider/types';

import { useIsAIEnabledForContent } from '@confluence/ai-common/entry-points/useIsAIEnabledForContent.ts';

/**
 * Minimal shape of the editor API needed by the skill-selected callback.
 * Uses an index signature so callers can pass any editor preset API
 * without needing an exact structural match.
 */
type SkillSelectedEditorAPI = Record<string, unknown> | null;

type UseRovoSkillsConfigParams = {
	/** The Atlassian cloud ID for the current site. */
	cloudId: string;
	/** Whether Rovo AI is enabled for the user. */
	isRovoEnabled: boolean;
	/** The editor API reference, used to call handleSkillSelected. */
	editorAPI: SkillSelectedEditorAPI | null | undefined;
	/** Content ID for checking AI content enablement. */
	contentId: string;
	/** Whether to show all available skills. Defaults to `true`. */
	showAllSkills?: boolean;
	/** Explicit list of skill slugs to show. When set, only these skills appear. */
	allowedSkills?: string[];
	/** Custom handler for when a skill is selected. Defaults to the AI plugin's `handleSkillSelected` action. */
	onSkillSelected?: (slashCommand: string, skill: unknown) => void;
};

/**
 * Returns a `RovoSkillsInsertMenuConfig` for use in editor insert menus.
 * Handles gating, cloudId, and wires `onSkillSelected` to the AI plugin's
 * `handleSkillSelected` action via the provided editor API.
 *
 * Usage:
 * ```ts
 * const rovoSkillsConfig = useRovoSkillsConfig({ cloudId, isRovoEnabled, editorAPI, contentId });
 * ```
 */
export function useRovoSkillsConfig({
	cloudId,
	isRovoEnabled,
	editorAPI,
	contentId,
	showAllSkills = true,
	allowedSkills,
	onSkillSelected,
}: UseRovoSkillsConfigParams): RovoSkillsInsertMenuConfig | undefined {
	const { isAIEnabledForContent } = useIsAIEnabledForContent({ contentId });

	// Keep a ref to the latest editorAPI so the callback always reads the
	// current value at invocation time, regardless of when the config object
	// was memoised or cached by upstream consumers (e.g. useExtensionOptions).
	const editorAPIRef = useRef(editorAPI);
	editorAPIRef.current = editorAPI;

	return useMemo(() => {
		if (
			!isRovoEnabled ||
			!isAIEnabledForContent ||
			!cloudId ||
			!expValEquals('platform_editor_insert_menu_ai', 'isEnabled', true)
		) {
			return undefined;
		}

		const handleSkillSelected =
			onSkillSelected ??
			((slashCommand: string, skill: unknown) => {
				setTimeout(() => {
					const api = editorAPIRef.current as
						| {
								core?: {
									actions: {
										execute: (command: unknown) => void;
									};
								};
								aiExperience?: {
									actions: {
										handleSkillSelected: (slashCommand: string, skill: unknown) => unknown;
									};
								};
						  }
						| null
						| undefined;
					const command = api?.aiExperience?.actions.handleSkillSelected(slashCommand, skill);
					if (command !== undefined) {
						api?.core?.actions.execute(command);
					}
				}, 0);
			});

		return {
			enabled: true,
			cloudId,
			showAllSkills,
			allowedSkills,
			onSkillSelected: handleSkillSelected,
		};
	}, [
		isRovoEnabled,
		isAIEnabledForContent,
		cloudId,
		showAllSkills,
		allowedSkills,
		onSkillSelected,
	]);
}
